import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ShopSelector from '@/components/selectors/ShopSelector';
import ModuleSelector from '@/components/selectors/ModuleSelector';
import DateRangeSelector from '@/components/selectors/DateRangeSelector';
import type { ModuleType } from './types';
import { useStore, useSettingsStore } from '@core/store';
import { shallow } from 'zustand/shallow';
import IntervalComplexSelector from '@/components/selectors/IntervalComplexSelector.tsx';
import { WcnButton } from '@/components';
import { useShopPersistence } from '@core/hooks/usePersistence';

export interface ModuleRangeIntervalData<T = number | string> {
    selectedShopId: string;
    start: string;
    end: string;
    deviceId: string;
    moduleId: string;
    interval: T;
}

export interface FilterModuleRangeIntervalProps<T = number | string> {
    forType?: string;
    moduleType?: string;
    intervals?: T[];
    interval?: T;
    defaultInterval?: T;
    maxRange?: number;
    onLoadData: (data: ModuleRangeIntervalData<T>) => void;
}

const FilterModuleRangeInterval = <T extends number | string = number>({
    forType,
    moduleType,
    intervals = [5, 10, 15, 30] as T[],
    interval,
    defaultInterval,
    maxRange = 30,
    onLoadData
}: FilterModuleRangeIntervalProps<T>) => {
    const { t, i18n } = useTranslation();
    const { darkMode } = useSettingsStore();
    const [searchParams] = useSearchParams();

    // Use forType or moduleType
    const actualModuleType = forType || moduleType || 'zoneTracker';
    const { selectedShopIds, setSelectedShopIds } = useShopPersistence();
    const [moduleId, setModuleId] = useState<string | null>(null);
    const [startDay, setStartDay] = useState<string>(DateTime.local().minus({ days: 7 }).toISODate() ?? '');
    const [endDay, setEndDay] = useState<string>(DateTime.local().toISODate() ?? '');
    const [selectedInterval, setSelectedInterval] = useState<T>(defaultInterval ?? interval ?? intervals[0]);

    const hasInitialized = useRef(false);

    // Get module data from store
    const {
        trolleyShelterIds,
        zoneTrackerIds,
        lineTrackerIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds
    } = useStore(
        (state) => ({
            trolleyShelterIds: state.trolleyShelterIds,
            zoneTrackerIds: state.zoneTrackerIds,
            lineTrackerIds: state.lineTrackerIds,
            loadTrolleyShelterIds: state.loadTrolleyShelterIds,
            loadZoneTrackerIds: state.loadZoneTrackerIds,
            loadLineTrackerIds: state.loadLineTrackerIds
        }),
        shallow
    );

    // Load module data
    useEffect(() => {
        const loadModuleData = async () => {
            const normalizedType = actualModuleType.toLowerCase();

            if (normalizedType.includes('trolleyshelter') && (!trolleyShelterIds || trolleyShelterIds.length === 0)) {
                await loadTrolleyShelterIds();
            } else if (normalizedType.includes('zonetracker') && (!zoneTrackerIds || zoneTrackerIds.length === 0)) {
                await loadZoneTrackerIds();
            } else if (normalizedType.includes('linetracker') && (!lineTrackerIds || lineTrackerIds.length === 0)) {
                await loadLineTrackerIds();
            }
        };

        loadModuleData();
    }, [
        actualModuleType,
        trolleyShelterIds,
        zoneTrackerIds,
        lineTrackerIds,
        loadTrolleyShelterIds,
        loadZoneTrackerIds,
        loadLineTrackerIds
    ]);

    useEffect(() => {
        if (!selectedShopIds[0] || !moduleId) return;
        emitLoadData();
    }, [selectedShopIds[0], moduleId]);

    // Filter modules
    const filteredModules = useMemo(() => {
        if (!selectedShopIds[0]) return [];

        const normalizedType = actualModuleType.toLowerCase();
        let moduleList: any[] = [];

        if (normalizedType.includes('trolleyshelter')) {
            moduleList = trolleyShelterIds;
        } else if (normalizedType.includes('zonetracker')) {
            moduleList = zoneTrackerIds;
        } else if (normalizedType.includes('linetracker')) {
            moduleList = lineTrackerIds;
        }

        return moduleList.filter(
            (module) => module.selectedShopId === selectedShopIds[0] && !module.disabled && !module.deleteDate
        );
    }, [selectedShopIds, actualModuleType, trolleyShelterIds, zoneTrackerIds, lineTrackerIds]);

    // Auto-select first module when filtered modules change
    useEffect(() => {
        if (filteredModules.length > 0 && !moduleId) {
            setModuleId(filteredModules[0].id);
        } else if (filteredModules.length > 0 && moduleId && !filteredModules.some((m) => m.id === moduleId)) {
            setModuleId(null);
        }
    }, [filteredModules, moduleId]);

    const selectedModule = useMemo(() => {
        return filteredModules.find((m) => m.id === moduleId) || null;
    }, [filteredModules, moduleId]);

    const errorMessages = useMemo(() => {
        if (!startDay || !endDay) return [];

        const start = DateTime.fromISO(startDay, { zone: 'UTC' });
        const end = DateTime.fromISO(endDay, { zone: 'UTC' });

        if (!start.isValid || !end.isValid) return [];

        const daysDiff = Math.abs(end.diff(start, 'days').days);
        return daysDiff <= maxRange
            ? []
            : [t('errors.rangeLtDays', { days: maxRange }) || `Range must be less than ${maxRange} days`];
    }, [startDay, endDay, maxRange, t]);

    const loadDisabled = useMemo(() => {
        return !selectedShopIds[0] || !moduleId || !startDay || !endDay || errorMessages.length > 0;
    }, [selectedShopIds, moduleId, startDay, endDay, errorMessages]);

    // Handle route query parameters - Only run once
    useEffect(() => {
        if (hasInitialized.current) return;

        const shopIdParam = searchParams.get('selectedShopId');
        const moduleIdParam = searchParams.get('moduleId');
        const startParam = searchParams.get('start');
        const endParam = searchParams.get('end');
        const intervalParam = searchParams.get('interval');

        if (shopIdParam) {
            setSelectedShopIds([shopIdParam]);
        }

        if (moduleIdParam) {
            setModuleId(moduleIdParam);
        }

        if (startParam) {
            const startDate = DateTime.fromISO(startParam, { zone: 'UTC' }).toISODate();
            if (startDate !== null) setStartDay(startDate);
        }

        if (endParam) {
            const endDate = DateTime.fromISO(endParam, { zone: 'UTC' }).toISODate();
            if (endDate !== null) setEndDay(endDate);
        }

        if (intervalParam) {
            const parsedInterval = intervalParam.includes(':') ? intervalParam : parseInt(intervalParam, 10);
            setSelectedInterval(parsedInterval as T);
        }

        hasInitialized.current = true;
    }, []); // Empty deps - only run on mount

    // Stable callbacks
    const handleShopChange = useCallback((value: string | null) => {
        setSelectedShopIds(value ? [value] : []);
        setModuleId(null); // Reset module when shop changes, auto-select will handle it
    }, []);

    const handleModuleChange = useCallback((selectedModuleId: string | null) => {
        setModuleId(selectedModuleId);
    }, []);

    const handleStartDayChange = useCallback((date: string) => {
        setStartDay(date);
    }, []);

    const handleEndDayChange = useCallback((date: string) => {
        setEndDay(date);
    }, []);

    const handleIntervalChange = useCallback((newInterval: T) => {
        setSelectedInterval(newInterval);
    }, []);

    const emitLoadData = useCallback(() => {
        if (!selectedModule || !selectedShopIds[0]) return;

        const start = DateTime.fromISO(startDay, { zone: 'UTC' }).startOf('day').toISO();
        const end = DateTime.fromISO(endDay, { zone: 'UTC' }).endOf('day').toISO();

        if (start !== null && end !== null) {
            onLoadData({
                selectedShopId: selectedShopIds[0],
                start,
                end,
                deviceId: selectedModule.deviceId,
                moduleId: selectedModule.id,
                interval: selectedInterval
            });
        }
    }, [selectedModule, selectedShopIds, startDay, endDay, selectedInterval, onLoadData]);

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={i18n.language}>
            <Box
                sx={{
                    display: 'flex',
                    gap: 3.5,
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    p: 3,
                    bgcolor: darkMode ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    mb: 3
                }}
            >
                {/* Shop Selector */}
                <Box sx={{ flex: '0 0 auto' }}>
                    <ShopSelector value={selectedShopIds[0] ?? null} onChange={handleShopChange} label={t('shop')} />
                </Box>

                {/* Module Selector */}
                <Box sx={{ width: 250, flex: '0 0 auto' }}>
                    <ModuleSelector
                        value={moduleId}
                        onChange={handleModuleChange}
                        shopId={selectedShopIds[0] ?? null}
                        moduleType={actualModuleType as ModuleType}
                        label={t(actualModuleType)}
                        modules={filteredModules}
                    />
                </Box>

                {/* Date Range Selector */}
                <DateRangeSelector
                    startDate={startDay}
                    endDate={endDay}
                    onStartDateChange={handleStartDayChange}
                    onEndDateChange={handleEndDayChange}
                    maxRange={maxRange}
                    locale={i18n.language}
                    error={errorMessages.length > 0}
                    helperText={errorMessages.length > 0 ? errorMessages[0] : undefined}
                />

                {/* Interval Selector */}
                <Box sx={{ width: 150, flex: '0 0 auto' }}>
                    <IntervalComplexSelector
                        value={selectedInterval}
                        onChange={handleIntervalChange}
                        intervals={intervals}
                        unit="minutes"
                    />
                </Box>

                {/* Load Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', pt: 2.5, ml: 'auto' }}>
                    <WcnButton variant="primary" disabled={loadDisabled} onClick={emitLoadData}>
                        {t('load')}
                    </WcnButton>
                </Box>
            </Box>
        </LocalizationProvider>
    );
};

export default FilterModuleRangeInterval;
